import React from 'react';
import {
  Box,
  Typography,
  Link as MuiLink,
  Grid,
  Divider,
} from '@mui/material';

import { Link } from 'react-router-dom';

import PhoneIcon from '@mui/icons-material/Phone';
import PrintIcon from '@mui/icons-material/Print';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import Logo from '../assets/logo.png';

const Footer = () => (
  <Box
    component="footer"
    sx={{
      backgroundColor: '#111',
      color: 'white',
      pt: 6,
      pb: 3,
      px: { xs: 3, md: 6 },
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box',
    }}
  >
    <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
      <Grid container spacing={5}>
        {/* Logo + Description */}
        <Grid item xs={12} md={4}>
          <Box
            component="img"
            src={Logo}
            alt="Aspire Behavioral Health logo"
            sx={{
              height: 50,
              mb: 2,
            }}
          />

          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.75)',
              lineHeight: 1.9,
              maxWidth: '320px',
            }}
          >
            Aspire Behavioral Health provides compassionate psychiatric care,
            medication management, ADHD testing, telehealth psychiatry, TMS
            therapy, and mental health services in San Antonio, Texas.
          </Typography>
        </Grid>

        {/* Quick Links */}
        <Grid item xs={12} sm={6} md={2}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: '1rem',
            }}
          >
            Quick Links
          </Typography>

          <FooterLink to="/">Home</FooterLink>
          <FooterLink to="/about-us">About Us</FooterLink>
          <FooterLink to="/insurance">Insurance</FooterLink>
          <FooterLink to="/faq">FAQ</FooterLink>
          <FooterLink to="/schedule-appointment">
            Request Appointment
          </FooterLink>
        </Grid>

        {/* Services */}
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: '1rem',
            }}
          >
            Services
          </Typography>

          <FooterLink to="/psychiatry">Psychiatry</FooterLink>
          <FooterLink to="/telehealth">Telehealth Psychiatry</FooterLink>
          <FooterLink to="/qbtest">ADHD Testing</FooterLink>
          <FooterLink to="/tms">TMS Therapy</FooterLink>
          <FooterLink to="/esketamine">SPRAVATO® Treatment</FooterLink>
          <FooterLink to="/suboxone">Suboxone® Treatment</FooterLink>
        </Grid>

        {/* Contact */}
        <Grid item xs={12} md={3}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: '1rem',
            }}
          >
            Contact Us
          </Typography>

          <ContactItem icon={<LocationOnIcon fontSize="small" />}>
            <MuiLink
              href="https://maps.app.goo.gl/9UV9FfwReJi69fRf9"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
            >
              14855 Blanco Rd, Suite 355
              <br />
              San Antonio, TX 78216
            </MuiLink>
          </ContactItem>

          <ContactItem icon={<PhoneIcon fontSize="small" />}>
            <MuiLink
              href="tel:2108669850"
              underline="hover"
              color="inherit"
            >
              210-866-9850
            </MuiLink>
          </ContactItem>

          <ContactItem icon={<PrintIcon fontSize="small" />}>
            210-866-7054
          </ContactItem>

          <ContactItem icon={<EmailIcon fontSize="small" />}>
            <MuiLink
              href="mailto:admin@aspirebhp.com"
              underline="hover"
              color="inherit"
            >
              admin@aspirebhp.com
            </MuiLink>
          </ContactItem>
        </Grid>
      </Grid>

      <Divider
        sx={{
          borderColor: 'rgba(255,255,255,0.12)',
          my: 4,
        }}
      />

      {/* Bottom Footer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: {
            xs: 'flex-start',
            md: 'center',
          },
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          gap: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255,255,255,0.6)',
          }}
        >
          © {new Date().getFullYear()} Aspire Behavioral Health. All rights
          reserved.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexWrap: 'wrap',
          }}
        >
          <MuiLink
            component={Link}
            to="/privacy"
            sx={footerBottomLink}
          >
            Privacy Policy
          </MuiLink>

          <MuiLink
            component={Link}
            to="/faq"
            sx={footerBottomLink}
          >
            FAQ
          </MuiLink>

          <MuiLink
            component={Link}
            to="/schedule-appointment"
            sx={footerBottomLink}
          >
            Request Appointment
          </MuiLink>
        </Box>
      </Box>
    </Box>
  </Box>
);

const FooterLink = ({ to, children }) => (
  <MuiLink
    component={Link}
    to={to}
    underline="none"
    sx={{
      display: 'block',
      mb: 1.2,
      color: 'rgba(255,255,255,0.75)',
      transition: '0.2s ease',
      fontSize: '0.95rem',
      '&:hover': {
        color: '#dfe8d3',
      },
    }}
  >
    {children}
  </MuiLink>
);

const ContactItem = ({ icon, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.2,
      mb: 2,
      color: 'rgba(255,255,255,0.75)',
      fontSize: '0.95rem',
      lineHeight: 1.7,
    }}
  >
    {icon}

    <Typography
      variant="body2"
      sx={{
        color: 'inherit',
        lineHeight: 1.7,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const footerBottomLink = {
  color: 'rgba(255,255,255,0.7)',
  textDecoration: 'none',
  fontSize: '0.9rem',
  transition: '0.2s ease',

  '&:hover': {
    color: '#dfe8d3',
  },
};

export default Footer;